/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Chip from '@ion/dls/src/components/chip/Chip';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DTCCodes, DtcEventType} from '@ion/dls/src/types/types';

export interface FaultChipDataProps {
  events: any;
  eventType: DtcEventType;
};

const toggleStyles = (style1: string, style2: string) =>
  ({eventType}: {eventType: string}) => eventType === DtcEventType.Dtc2Events? style1 : style2
;

const useStyles = makeStyles(() => {
  return {
    selectedDetailsWrapper : {
      display        : 'flex',
      flexFlow       : 'wrap',
      alignItems     : 'center',
      justifyContent : 'flex-end'
    },
    subDetailsWrapper : {
      width          : toggleStyles('170px', '181px'),
      display        : 'flex',
      flexFlow       : 'wrap',
      alignItems     : 'center',
      justifyContent : toggleStyles('flex-end', 'flex-start')
    },
    dtc2Events : {
      marginBottom : '10px',
      flex         : '-1 -1 33.333333%'
    },
    dtcEvents : {
      marginBottom : '10px'
    }
  };
});

/**
 * Defines the content in chips view for Faults data in Asset Details Diagnostic Accordian. This component is
 * intended to be used within the context of the Asset Detail -> Diagnostic -> Faults data
 * display on the iOn Suite tracking page.
 *
 * ```typescript
 * import FaultChipData from './FaultChipData';
 *
 * <FaultChipData events={faultData}
            eventType={dtcEventType} />
 *
 * ```
 */
export const FaultChipData = ({
  events,
  eventType
}: FaultChipDataProps) => {
  const [chipData, setChipData] = useState([]);
  const classes                 = useStyles({eventType});

  useEffect(() => {
    if (eventType === DtcEventType.Dtc2Events) {
      const formattedArray = [];
      events.forEach(data => Object.keys(data).forEach((key) => {
        const dtcCode = data[key];
        switch (key) {
          case DTCCodes.SPN :
            formattedArray.push({dtcCode, description: data.spnDescription});
            break;
          case DTCCodes.SourceAddress:
            formattedArray.push({dtcCode, description: translate('tracking:assetDetail.diagnostics.sourceAddress')});
            break;
          case DTCCodes.FMI :
            formattedArray.push({dtcCode, description: data.fmiDescription});
            break;
        }
      }));
      setChipData(formattedArray);
    } else {
      setChipData(events);
    }
  }, [events]);

  function ChipCustomTooltip(props) {
    return <Tooltip arrow {...props} />;
  }

  return (
    <div className={classes.selectedDetailsWrapper}>
      <div className={classes.subDetailsWrapper}>
        { chipData?.map?.(({description, dtcCode}, index) => (
          <ChipCustomTooltip title={description || '' } placement='top' key={index}>
            <div className={classes.dtcEvents}>
              <Chip label={dtcCode}
                size='small'
                maxWidth={false}
                borderRadius='4px'
                labelColor={dlsColor('grey2')}
                showCustomDeleteIcon
                hoverDefault
                isCustomRoot />
            </div>
          </ChipCustomTooltip>
        ))}
      </div>
    </div>);
};

export default memo(FaultChipData);
